import { type BreakoutUser } from '@breakoutlearning/firebase-repository/models/BreakoutUser'
import type { Section } from '@breakoutlearning/firebase-repository/models/Section'
import {
  APPROVED_FREE_PROMOTION_NAME,
  N_FREE_SECTIONS_PROMOTION_ID,
  type UserPromotion,
} from '@breakoutlearning/firebase-repository/models/UserPromotion'
import type { UserPromotionRedemption } from '@breakoutlearning/firebase-repository/models/UserPromotionRedemption'
import { SectionState } from '@breakoutlearning/firebase-repository/types'
import classNames from 'classnames'
import {
  BreakoutButton,
  type ButtonKind,
} from 'components/design-system/BreakoutButton'
import { BreakoutSelect } from 'components/design-system/BreakoutSelect'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { FormLabel } from 'components/design-system/form/FormLabel'
import { TrashCanIcon } from 'components/icons/TrashCan'
import { useUserPromotions } from 'hooks/useUserPromotions'
import { useTranslationTyped } from 'i18n/i18n'
import { DateTime } from 'luxon'
import { observer } from 'mobx-react-lite'
import { type ForwardedRef, useEffect, useMemo, useState } from 'react'

export const PromotionSelectionWidget = observer(
  function PromotionSelectionWidget(
    {
      kind = 'secondary',
      buttonKind,
      disabled = false,
      selectClassName,
      section,
      onChange,
      showAdminFreeSectionOption = false,
    }: {
      kind?: 'secondary' | 'tertiary'
      buttonKind?: ButtonKind
      disabled?: boolean
      selectClassName?: string
      section?: Section
      onChange?: (ids: string[]) => void
      /**
       * if showAdminFreeSectionOption is true, an option to create and apply a special one-time
       * promotion for the section will be shown.
       *
       * enable to know whether the option has been selected, the `applyAdminSpecialPromotion` property
       * of the `useUserPromotions` hook should be used. if true apply the special promotion
       */
      showAdminFreeSectionOption?: boolean
    },
    ref: ForwardedRef<
      | {
          selectedUserPromotions: UserPromotion[]
          applyAdminSpecialPromotion: boolean
          user: BreakoutUser
        }
      | undefined
    >
  ) {
    const { tt } = useTranslationTyped()

    const {
      user: promotionUser,
      applyAdminSpecialPromotion,
      hasPromotions,
      selectedUserPromotion,
      promotionIsActive,
      promotionIsConsumable,
      selectedUserPromotionIds,
      userPromotionOptions,
      userPromotions,
      userPromotionRedemptions,
      userPromotionRedemptionsArray,
      setSelectedUserPromotionIds,
      setSelectedUserPromotion,
      setApplyAdminSpecialPromotion,
    } = useUserPromotions(section)

    useEffect(() => {
      if (!ref || !('current' in ref)) return
      ref.current = {
        selectedUserPromotions: userPromotions.filter((p) =>
          selectedUserPromotionIds.includes(p.id)
        ),
        applyAdminSpecialPromotion,
        user: promotionUser,
      }
    }, [
      applyAdminSpecialPromotion,
      ref,
      selectedUserPromotionIds,
      promotionUser,
      userPromotions,
    ])

    const redemptionsForSection = useMemo(() => {
      if (!section) return []
      return userPromotionRedemptionsArray.filter(
        (redemption) => redemption.data.sectionId === section.id
      )
    }, [section, userPromotionRedemptionsArray])

    const approvedFreeIsAppliedToSection = useMemo(() => {
      if (!section) return false
      return userPromotions.some((userPromotion) => {
        return (
          userPromotion.data.promotionName === APPROVED_FREE_PROMOTION_NAME &&
          redemptionsForSection.some(
            (r) => r.data.userPromotionId === userPromotion.id
          )
        )
      })
    }, [redemptionsForSection, section, userPromotions])

    const redeemedOrSelectedPromotionIds = useMemo(() => {
      const promotionIds = new Set<string>()

      for (const promotionId of selectedUserPromotionIds) {
        const promotion = userPromotions.find((p) => p.id === promotionId)
        if (promotion) promotionIds.add(promotion.data.promotionId)
      }

      for (const redemption of redemptionsForSection) {
        promotionIds.add(redemption.data.promotionId)
      }

      if (applyAdminSpecialPromotion)
        promotionIds.add(N_FREE_SECTIONS_PROMOTION_ID)
      return promotionIds
    }, [
      applyAdminSpecialPromotion,
      redemptionsForSection,
      selectedUserPromotionIds,
      userPromotions,
    ])

    const promotionIdsAlreadyAppliedToSection = useMemo(
      () => redemptionsForSection.map((r) => r.data.userPromotionId),
      [redemptionsForSection]
    )

    const [selectAdminSpecialPromotion, setSelectAdminSpecialPromotion] =
      useState(false)

    const selectedPromotionId = useMemo(() => {
      if (selectedUserPromotion) {
        const selected = userPromotions.find(
          (p) => p.id === selectedUserPromotion
        )
        if (selected) return selected.data.promotionId
      }
      if (selectAdminSpecialPromotion) return N_FREE_SECTIONS_PROMOTION_ID
      return ''
    }, [selectAdminSpecialPromotion, selectedUserPromotion, userPromotions])

    const promotionSelectOptions = useMemo(() => {
      if (!showAdminFreeSectionOption) return userPromotionOptions

      const groupedOptionsForAdmin = [
        {
          groupLabel: tt.instructor_library.admin_promotions(),
          options: [
            {
              label: tt.instructor_library.approved_free_promotion(),
              value: 'adminFreeSectionOption',
            },
          ],
        },
      ]

      if (userPromotionOptions.length)
        groupedOptionsForAdmin.push({
          groupLabel: tt.instructor_library.instructor_promotions(),
          options: userPromotionOptions,
        })

      return groupedOptionsForAdmin
    }, [
      showAdminFreeSectionOption,
      tt.instructor_library,
      userPromotionOptions,
    ])

    const alreadyHasStudents = section && section.userCount > 0

    const sectionIsCompleted =
      section?.data.sectionState === SectionState.completed

    const hasPromotionSelected =
      selectAdminSpecialPromotion || selectedUserPromotion !== null

    let tooltip: string | undefined = undefined

    if (sectionIsCompleted) {
      tooltip = tt.instructor_library.promotions_disabled_section_completed()
    } else if (alreadyHasStudents) {
      tooltip = tt.instructor_library.promotions_disabled_tooltip()
    } else if (selectedUserPromotion && !promotionIsActive) {
      tooltip = tt.instructor_library.promotion_not_active()
    } else if (selectedUserPromotion && !promotionIsConsumable) {
      tooltip = tt.instructor_library.promotion_not_consumable()
    } else if (section?.usesSectionPass) {
      tooltip = tt.instructor_library.promotions_disabled_section_pass()
    } else if (redeemedOrSelectedPromotionIds.has(selectedPromotionId)) {
      tooltip = tt.instructor_library.promotion_of_type_already_applied()
    }

    if (
      !hasPromotions &&
      !showAdminFreeSectionOption &&
      !promotionIdsAlreadyAppliedToSection.length
    )
      return null

    return (
      <div className="flex-1">
        <div className="flex flex-col items-start gap-1">
          <FormLabel label={tt.instructor_library.promotions_label()} />
          <div className="flex w-full flex-row items-center justify-between gap-4">
            <BreakoutSelect
              inputClassName={selectClassName}
              fullWidth
              kind={kind}
              value={
                selectAdminSpecialPromotion
                  ? 'adminFreeSectionOption'
                  : selectedUserPromotion
              }
              loading={!promotionUser.promotionsAndRedemptionsLoaded}
              onChange={(value) => {
                if (value === 'adminFreeSectionOption') {
                  setSelectedUserPromotion(null)
                  return setSelectAdminSpecialPromotion(true)
                }
                setSelectAdminSpecialPromotion(false)
                setSelectedUserPromotion(value)
              }}
              aria-label={tt.instructor_library.select_promotion()}
              className="!w-full"
              options={promotionSelectOptions}
              placeholder={
                promotionSelectOptions.length
                  ? tt.instructor_library.select_promotion()
                  : tt.instructor_library.no_promotions_remaining()
              }
              disabled={
                disabled ||
                !promotionSelectOptions.length ||
                sectionIsCompleted ||
                !promotionUser.promotionsAndRedemptionsLoaded
              }
            />
            <BreakoutButton
              size="medium"
              disabled={
                section?.usesSectionPass ||
                !hasPromotionSelected ||
                (selectedUserPromotion && !promotionIsConsumable) ||
                (selectedUserPromotion && !promotionIsActive) ||
                alreadyHasStudents ||
                sectionIsCompleted ||
                redeemedOrSelectedPromotionIds.has(selectedPromotionId)
              }
              kind={buttonKind ?? kind}
              className="!w-[75px] !min-w-[0px] !max-w-[75px] !rounded-xl"
              tooltip={tooltip}
              onClick={() => {
                if (selectAdminSpecialPromotion) {
                  setSelectAdminSpecialPromotion(false)
                  return setApplyAdminSpecialPromotion(true)
                }

                if (!promotionIsConsumable || section?.usesSectionPass) return
                if (selectedUserPromotion) {
                  const newValues = [
                    ...selectedUserPromotionIds,
                    selectedUserPromotion,
                  ]
                  onChange?.(newValues)
                  setSelectedUserPromotionIds(newValues)
                  setSelectedUserPromotion(null)
                }
              }}
            >
              {tt.instructor_library.apply()}
            </BreakoutButton>
          </div>
          <div className="flex max-h-[350px] w-full flex-col gap-2 overflow-auto">
            {applyAdminSpecialPromotion && !approvedFreeIsAppliedToSection && (
              <UserPromotionRow
                userPromotion={'applySpecialAdminPromotion'}
                redemptions={[]}
                onRemove={() => setApplyAdminSpecialPromotion(false)}
                disabled={disabled}
              />
            )}
            {[
              // filter promotions applied from selected, this prevents duplicates from appearing
              // when the redemption has been created but dialog has not yet closed
              ...selectedUserPromotionIds
                .filter(
                  (id) => !promotionIdsAlreadyAppliedToSection.includes(id)
                )
                .map((id) => ({ applied: false, id })),
              ...promotionIdsAlreadyAppliedToSection.map((id) => ({
                applied: true,
                id,
              })),
            ].map(({ applied, id }) => {
              const userPromotion = userPromotions?.find((p) => p.id === id)
              const redemptions = userPromotionRedemptions?.get(id) || []
              if (!userPromotion) return null

              return (
                <UserPromotionRow
                  disabled={disabled}
                  key={id}
                  userPromotion={userPromotion}
                  redemptions={redemptions}
                  onRemove={
                    !applied
                      ? () => {
                          const newValues = selectedUserPromotionIds.filter(
                            (i) => i !== id
                          )
                          onChange?.(newValues)
                          setSelectedUserPromotionIds(newValues)
                        }
                      : undefined
                  }
                />
              )
            })}
          </div>
        </div>
      </div>
    )
  },
  { forwardRef: true }
)

const UserPromotionRow = observer(function UserPromotionRow({
  userPromotion,
  redemptions,
  onRemove,
  disabled,
}: {
  userPromotion: UserPromotion | 'applySpecialAdminPromotion'
  redemptions: UserPromotionRedemption[]
  disabled: boolean
  onRemove?: () => void
}) {
  const { tt } = useTranslationTyped()

  const {
    promotionName,
    consumableCount,
    isActive,
    consumed,
    expirationDateTime,
  } = useMemo(() => {
    if (typeof userPromotion !== 'string') {
      const { expirationDate, promotionName, consumableCount } =
        userPromotion.data
      const isActive = userPromotion.isActive
      const consumed = redemptions.length
      const expirationDateTime = DateTime.fromJSDate(expirationDate)
      return {
        promotionName,
        consumableCount,
        isActive,
        consumed,
        expirationDateTime,
      }
    }

    return {
      promotionName: APPROVED_FREE_PROMOTION_NAME,
      consumableCount: 1,
      consumed: 1,
      expirationDateTime: DateTime.now().plus({ days: 1 }),
      isActive: true,
    }
  }, [userPromotion, redemptions])

  return (
    <div className="flex flex-row  items-center justify-between rounded-2xl bg-surface p-5">
      <div>
        <div
          className={classNames('text-label-small uppercase', {
            'text-core-error': !isActive,
            'text-core-success': isActive,
          })}
        >
          {isActive ? tt.profile.active() : tt.profile.inactive()}
        </div>
        <div className="text-label-medium">{promotionName}</div>
        <div className="text-body-small text-on-surface-var">
          {tt.profile.consumed_x_of_y({
            consumed,
            total: consumableCount,
          })}
        </div>
        <div className="text-body-small text-on-surface-var">
          {tt.profile.expires_at({
            expirationDate: expirationDateTime.toLocaleString(
              DateTime.DATE_FULL
            ),
          })}
        </div>
      </div>
      {onRemove && (
        <BreakoutTooltip
          content={tt.instructor_library.delete_promotion_tooltip()}
          className="max-w-[330px]"
        >
          <div className="text-center">
            <TrashCanIcon
              onClick={!disabled ? onRemove : undefined}
              size={15}
              className={classNames({
                'text-on-surface-disabled': disabled,
                'cursor-pointer': !disabled,
              })}
            />
          </div>
        </BreakoutTooltip>
      )}
    </div>
  )
})
